import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import U2000AutoCompleteInput from '../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput';
import { NS } from '../../../U2101';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { TFunction } from 'i18next';
import U2000CheckBoxArray from '../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxArray';
import { LOGICAL_UNIT_TYPES, type LogicalUnitType } from '../../common/logicalUnitTypes';
import { useTranslation } from 'react-i18next';
import { useU2101FindLogicalUnitCategoryHandlers } from '../../../U2101UseFindLogicalUnitCategoryHandlers';
import type { U2101FindLogicalUnitCategoryItem } from '../../../U2101Service';

interface U2101LogicalUnitComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
}

function formatCategoryLabel(item: U2101FindLogicalUnitCategoryItem | null | undefined, lang: string): string {
  if (!item) return '';
  const label = lang.startsWith('en') ? item.labelEn || item.labelFr : item.labelFr || item.labelEn;
  const code = item.code ?? '';
  if (code && label) return `${code} - ${label}`;
  return code || label || '';
}

function U2101LogicalUnitBody<T extends Record<string, any>>({ formStateHandlers, t }: U2101LogicalUnitComponentProps<T>) {
  const { i18n } = useTranslation();
  const { search } = useU2101FindLogicalUnitCategoryHandlers();

  const onSearchCategory = async (criterias: { fieldName: string; value: unknown }[]) => {
    return search(criterias);
  };

  const displayCategory = (item: U2101FindLogicalUnitCategoryItem | null | undefined) => {
    return formatCategoryLabel(item, i18n.language);
  };

  const extractCategoryData = (result: U2101FindLogicalUnitCategoryItem | null) => {
    if (!result?.id) {
      return [{ fieldName: 'logicalUnitCategory', value: null }];
    }
    return [
      { fieldName: 'logicalUnitCategory.id', value: result.id },
      { fieldName: 'logicalUnitCategory.code', value: result.code ?? '' },
      { fieldName: 'logicalUnitCategory.labelFr', value: result.labelFr ?? '' },
      { fieldName: 'logicalUnitCategory.labelEn', value: result.labelEn ?? '' },
      { fieldName: 'logicalUnitCategory.sortOrder', value: result.sortOrder ?? 0 },
    ];
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <U2000InputText fieldName="logicalUnitCode" label={t('lblLogicalUnitCode', { ns: NS })} formStateHandlers={formStateHandlers} />
      <U2000InputText fieldName="logicalUnitDescription" label={t('lblLogicalUnitDescription', { ns: NS })} formStateHandlers={formStateHandlers} />

      <div className="col-span-2">
        <U2000AutoCompleteInput
          formStateHandlers={formStateHandlers}
          fieldName="logicalUnitCategory"
          mainFieldName="code"
          label={t('lblLogicalUnitCategory', { ns: NS })}
          onSearch={onSearchCategory}
          displayFn={(item) => displayCategory(item as U2101FindLogicalUnitCategoryItem)}
          overrideExtractDataFn={(item) => extractCategoryData(item as U2101FindLogicalUnitCategoryItem | null)}
          minCarLaunchSearch={0}
          resultLabels={[]}
          resultFields={['code', i18n.language.startsWith('en') ? 'labelEn' : 'labelFr']}
          required={false}
        />
      </div>

      <div className="col-span-2 mt-2 flex flex-col gap-2">
        <div className="flex items-center gap-8">
          {LOGICAL_UNIT_TYPES.map((type: LogicalUnitType) => (
            <U2000CheckBoxArray
              formStateHandlers={formStateHandlers}
              key={`logical-unit-type-${type.value}`}
              fieldName="logicalUnitTypes"
              value={type.value}
              label={typeof type.label === 'object' ? type.label['fr'] : String(type.label)}
              readonly={false}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default U2101LogicalUnitBody;
